import { useApiError } from "@/ui/useApiError.ts";
import type { Chat, Project } from "@codvero/shared";
import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { Brain, Download, Archive, ArchiveRestore, ChevronDown, Eye, EyeOff, GitFork, MessageSquarePlus, MoreHorizontal, Pencil, Pin, PinOff, Plus, Search, Settings2, Trash2, X } from "lucide-react";
import { Menu } from "@/ui/Menu.tsx";
import { useConfirm } from "@/ui/ConfirmDialog.tsx";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { chatsApi } from "@/api/chats.ts";
import { projectsApi } from "@/api/projects.ts";
import { cn } from "@/lib/cn.ts";
import { saveToDevice } from "@/features/chat/FilePreview.tsx";
import { Button, Input } from "@/ui/primitives.tsx";

/** Where a chat opens: personal-space chats live in the Chat section, the others under their project. */
export const chatLink = (p: Pick<Project, "id" | "kind">, chatId: string) =>
  (p.kind === "personal" ? { to: "/chat/$chatId", params: { chatId } } : { to: "/projects/$projectId/chat/$chatId", params: { projectId: p.id, chatId } }) as { to: "/projects/$projectId/chat/$chatId"; params: { projectId: string; chatId: string } };

const EXPANDED_KEY = "codvero.sidebar.expanded";
function readExpanded(): Set<string> {
  try {
    return new Set(JSON.parse(localStorage.getItem(EXPANDED_KEY) ?? "[]") as string[]);
  } catch {
    return new Set();
  }
}

/** Projects with their chats, like the original's left sidebar. */
export function ProjectsSidebar({ onNavigate, onNewProject }: { onNavigate?: () => void; onNewProject: () => void }) {
  const { t } = useTranslation();
  const nav = useNavigate();
  const qc = useQueryClient();
  const params = useParams({ strict: false }) as { projectId?: string; chatId?: string };
  const projects = useQuery({ queryKey: ["projects"], queryFn: projectsApi.list, refetchInterval: 15_000 });
  const [q, setQ] = useState("");
  const [expanded, setExpanded] = useState<Set<string>>(readExpanded);
  useEffect(() => {
    if (params.projectId && !expanded.has(params.projectId)) setExpanded((e) => new Set(e).add(params.projectId!));
  }, [params.projectId]);
  useEffect(() => {
    try {
      localStorage.setItem(EXPANDED_KEY, JSON.stringify([...expanded]));
    } catch {
      /* ignore */
    }
  }, [expanded]);

  const list = projects.data ?? [];
  const chatQueries = useQueries({
    queries: list.filter((p) => expanded.has(p.id) || q).map((p) => ({ queryKey: ["chats", p.id, "list"], queryFn: () => chatsApi.list(p.id), refetchInterval: 10_000 })),
  });
  const chatsByProject = useMemo(() => {
    const m = new Map<string, Chat[]>();
    list.filter((p) => expanded.has(p.id) || q).forEach((p, i) => m.set(p.id, chatQueries[i]?.data ?? []));
    return m;
  }, [list, expanded, q, chatQueries]);

  const createChat = useMutation({
    mutationFn: (projectId: string) => chatsApi.create(projectId, {}),
    onSuccess: (c) => {
      void qc.invalidateQueries({ queryKey: ["chats", c.projectId, "list"] });
      setExpanded((e) => new Set(e).add(c.projectId));
      void nav({ to: "/projects/$projectId/chat/$chatId", params: { projectId: c.projectId, chatId: c.id } });
      onNavigate?.();
    },
  });
  const forkChat = useMutation({
    mutationFn: (c: Chat) => chatsApi.fork(c.id),
    onSuccess: (c) => {
      void qc.invalidateQueries({ queryKey: ["chats", c.projectId, "list"] });
      void nav({ to: "/projects/$projectId/chat/$chatId", params: { projectId: c.projectId, chatId: c.id } });
      onNavigate?.();
    },
  });
  const { confirm, prompt } = useConfirm();
  const updateChat = useMutation({
    mutationFn: ({ c, patch }: { c: Chat; patch: Parameters<typeof chatsApi.update>[1] }) => chatsApi.update(c.id, patch),
    onSuccess: (c) => void qc.invalidateQueries({ queryKey: ["chats", c.projectId, "list"] }),
  });
  const deleteChat = useMutation({
    mutationFn: (c: Chat) => chatsApi.remove(c.id),
    onSuccess: (_d, c) => {
      void qc.invalidateQueries({ queryKey: ["chats", c.projectId, "list"] });
      if (params.chatId === c.id) void nav({ to: "/projects/$projectId", params: { projectId: c.projectId } });
    },
  });

  const toMsg = useApiError();
  const sidebarError = createChat.error ?? forkChat.error ?? updateChat.error ?? deleteChat.error;

  const needle = q.trim().toLowerCase();
  const visible = list.filter((p) => !needle || p.name.toLowerCase().includes(needle) || p.slug.includes(needle) || (chatsByProject.get(p.id) ?? []).some((c) => c.title.toLowerCase().includes(needle)));
  const totalChats = [...chatsByProject.values()].reduce((n, a) => n + a.length, 0);

  return (
    <div className="flex h-full flex-col">
      <div className="space-y-2 p-3">
        <Button className="w-full justify-start" variant="secondary" onClick={onNewProject}><Plus className="size-4" /> {t("projects.new")}</Button>
        {sidebarError && <button type="button" className="w-full rounded-md border border-danger/40 bg-danger-soft px-2 py-1.5 text-start text-xs text-danger" onClick={() => { createChat.reset(); forkChat.reset(); updateChat.reset(); deleteChat.reset(); }}>{toMsg(sidebarError)} ✕</button>}
        <div className="relative">
          <Search className="pointer-events-none absolute start-2.5 top-1/2 size-3.5 -translate-y-1/2 text-fg-muted" />
          <Input data-sidebar-search className="h-8 ps-8 text-xs" placeholder={t("sidebar.search")} value={q} onChange={(e) => setQ(e.target.value)} />
          {q && <button type="button" className="absolute end-2 top-1/2 -translate-y-1/2 text-fg-muted" onClick={() => setQ("")}><X className="size-3.5" /></button>}
        </div>
        <div className="px-1 text-[11px] text-fg-muted">{t("sidebar.counts", { projects: list.length, chats: totalChats })}</div>
      </div>
      <nav className="min-h-0 flex-1 overflow-y-auto px-2 pb-3">
        {visible.length === 0 && <p className="px-2 py-6 text-center text-xs text-fg-muted">{t("projects.empty")}</p>}
        {visible.map((p) => (
          <ProjectRow
            key={p.id}
            project={p}
            open={expanded.has(p.id) || !!needle}
            active={params.projectId === p.id}
            chats={chatsByProject.get(p.id) ?? []}
            activeChatId={params.chatId ?? null}
            onToggle={() => setExpanded((e) => { const n = new Set(e); if (n.has(p.id)) n.delete(p.id); else n.add(p.id); return n; })}
            onNewChat={() => createChat.mutate(p.id)}
            onDeleteChat={async (c) => { if (await confirm({ title: t("chatMenu.deleteTitle"), message: t("chatMenu.deleteMsgProject", { title: c.title }), confirmLabel: t("chatMenu.delete"), cancelLabel: t("chatMenu.keep"), danger: true })) deleteChat.mutate(c); }}
            onForkChat={(c) => forkChat.mutate(c)}
            onRenameChat={async (c) => { const v = await prompt({ title: t("chatMenu.rename"), input: { initial: c.title, label: t("chat.rename") }, confirmLabel: t("common.save") }); if (v && v.trim() !== c.title) updateChat.mutate({ c, patch: { title: v.trim() } }); }}
            onPatchChat={(c, patch) => updateChat.mutate({ c, patch })}
            onNavigate={onNavigate}
            needle={needle}
          />
        ))}
      </nav>
    </div>
  );
}

function ProjectRow({ project: p, open, active, chats, activeChatId, onToggle, onNewChat, onDeleteChat, onForkChat, onRenameChat, onPatchChat, onNavigate, needle }: { project: Project; open: boolean; active: boolean; chats: Chat[]; activeChatId: string | null; onToggle: () => void; onNewChat: () => void; onDeleteChat: (c: Chat) => void; onForkChat: (c: Chat) => void; onRenameChat: (c: Chat) => void; onPatchChat: (c: Chat, patch: { pinned?: boolean; archived?: boolean; unread?: boolean }) => void; onNavigate?: (() => void) | undefined; needle: string }) {
  const { t, i18n } = useTranslation();
  const rel = (iso: string) => {
    const d = (Date.now() - new Date(iso).getTime()) / 1000;
    const rtf = new Intl.RelativeTimeFormat(i18n.language, { numeric: "auto" });
    if (d < 60) return rtf.format(-Math.round(d), "second");
    if (d < 3600) return rtf.format(-Math.round(d / 60), "minute");
    if (d < 86400) return rtf.format(-Math.round(d / 3600), "hour");
    return rtf.format(-Math.round(d / 86400), "day");
  };
  const shown = chats.filter((c) => !needle || c.title.toLowerCase().includes(needle) || p.name.toLowerCase().includes(needle));
  const [showArchived, setShowArchived] = useState(false);
  return (
    <div className="mb-1">
      <div className={cn("group flex items-center gap-1 rounded-md px-2 py-1.5", active && !activeChatId && "bg-accent-soft")}>
        <Link to="/projects/$projectId" params={{ projectId: p.id }} onClick={onNavigate} className="flex min-w-0 flex-1 items-center gap-2 text-sm" title={t(`projects.state.${p.state}`)}>
          <span className={cn("size-2 shrink-0 rounded-full", p.state === "running" ? "bg-success" : p.state === "error" ? "bg-danger" : p.state === "creating" ? "animate-pulse bg-warn" : "bg-fg-muted/50")} />
          <span className="truncate font-medium leading-5">{p.name}</span>
          {/* the server's counts keep the dots right while the project is collapsed and no chat of it is open */}
          {(p.activity?.running || chats.some((c) => c.status === "running")) ? (
            <span className="size-1.5 shrink-0 animate-pulse rounded-full bg-warn" title={t("chat.working")} />
          ) : (p.activity?.error || chats.some((c) => c.status === "error" && c.unread)) ? (
            <span className="size-1.5 shrink-0 rounded-full bg-danger" title={t("chat.runError")} />
          ) : null}
        </Link>
        <Link to="/projects/$projectId/settings" params={{ projectId: p.id }} onClick={onNavigate} aria-label={t("projects.tabs.settings")} className="invisible rounded p-0.5 text-fg-muted hover:text-fg group-hover:visible group-focus-within:visible [@media(pointer:coarse)]:visible [@media(pointer:coarse)]:p-1.5"><Settings2 className="size-3.5" /></Link>
        {p.myRole !== "viewer" && (
          <button type="button" aria-label={t("chat.new")} title={t("chat.new")} className="invisible rounded p-0.5 text-fg-muted hover:text-fg group-hover:visible group-focus-within:visible [@media(pointer:coarse)]:visible [@media(pointer:coarse)]:p-1.5" onClick={onNewChat}><MessageSquarePlus className="size-3.5" /></button>
        )}
        <button type="button" aria-label={open ? t("common.close") : t("sidebar.expand")} title={t("sidebar.expand")} className="rounded p-0.5 text-fg-muted hover:text-fg" onClick={onToggle}>
          <ChevronDown className={cn("size-3.5 transition-transform", !open && "-rotate-90 rtl:rotate-90")} />
        </button>
      </div>
      {open && (
        <ul className="ms-3 border-s border-border/80 ps-2">
          {shown.length === 0 && <li className="px-2 py-1 text-[11px] text-fg-muted">{t("chat.empty")}</li>}
          {shown.filter((c) => !c.archived).map((c) => <ChatRow key={c.id} c={c} p={p} activeChatId={activeChatId} rel={rel} onNavigate={onNavigate} onDeleteChat={onDeleteChat} onForkChat={onForkChat} onRenameChat={onRenameChat} onPatchChat={onPatchChat} />)}
          {shown.some((c) => c.archived) && (
            <li>
              <button type="button" className="flex w-full items-center gap-1.5 px-2 py-1 text-[11px] text-fg-muted hover:text-fg" onClick={() => setShowArchived((v) => !v)}>
                <Archive className="size-3" /> {t("chatMenu.archived", { count: shown.filter((c) => c.archived).length })}
                <ChevronDown className={cn("ms-auto size-3 transition-transform", !showArchived && "-rotate-90 rtl:rotate-90")} />
              </button>
              {showArchived && <ul>{shown.filter((c) => c.archived).map((c) => <ChatRow key={c.id} c={c} p={p} activeChatId={activeChatId} rel={rel} onNavigate={onNavigate} onDeleteChat={onDeleteChat} onForkChat={onForkChat} onRenameChat={onRenameChat} onPatchChat={onPatchChat} />)}</ul>}
            </li>
          )}
        </ul>
      )}
      
    </div>
  );
}

export function ChatRow({ c, p, activeChatId, rel, onNavigate, onDeleteChat, onForkChat, onRenameChat, onPatchChat, onShareMemory }: { c: Chat; p: Project; activeChatId: string | null; rel: (iso: string) => string; onNavigate?: (() => void) | undefined; onDeleteChat: (c: Chat) => void; onForkChat: (c: Chat) => void; onRenameChat: (c: Chat) => void; onPatchChat: (c: Chat, patch: { pinned?: boolean; archived?: boolean; unread?: boolean }) => void; onShareMemory?: ((c: Chat) => void) | undefined }) {
  const { t } = useTranslation();
  const canEdit = p.myRole !== "viewer";
  return (
    <li className="group/chat flex items-center gap-1">
      <Link {...chatLink(p, c.id)} onClick={onNavigate} className={cn("flex min-h-8 min-w-0 flex-1 items-center gap-1.5 rounded-md px-2 py-1 text-xs [@media(pointer:coarse)]:min-h-11 [@media(pointer:coarse)]:text-sm hover:bg-surface-2", c.id === activeChatId && "bg-accent-soft font-medium", c.archived && "opacity-60")}>
        {c.status === "running" ? <span className="size-1.5 shrink-0 animate-pulse rounded-full bg-warn" /> : c.status === "error" && c.unread ? <span className="size-1.5 shrink-0 rounded-full bg-danger" title={t("chat.runError")} /> : c.unread ? <span className="size-1.5 shrink-0 rounded-full bg-accent" title={t("chatMenu.unreadBadge")} /> : <span className="size-1.5 shrink-0 rounded-full bg-transparent" />}
        {c.pinned && <Pin className="size-3 shrink-0 text-fg-muted" />}
        <span className={cn("truncate", c.unread && "font-semibold")}>{c.title}</span>
        <span className="ms-auto shrink-0 text-[10px] text-fg-muted">{rel(c.updatedAt)}</span>
      </Link>
      {canEdit && (
        <Menu
          className="invisible group-hover/chat:visible group-focus-within/chat:visible [@media(pointer:coarse)]:visible [&:has([role=menu])]:visible"
          trigger={<button type="button" aria-label={t("chatMenu.open")} className="rounded-md border border-transparent px-1.5 py-0.5 text-fg-muted hover:border-border hover:bg-surface-2 hover:text-fg"><MoreHorizontal className="size-3.5" /></button>}
          items={[
            { label: t("chatMenu.rename"), icon: <Pencil className="size-4" />, onSelect: () => onRenameChat(c) },
            { label: c.pinned ? t("chatMenu.unpin") : t("chatMenu.pin"), icon: c.pinned ? <PinOff className="size-4" /> : <Pin className="size-4" />, onSelect: () => onPatchChat(c, { pinned: !c.pinned }) },
            { label: c.unread ? t("chatMenu.markRead") : t("chatMenu.markUnread"), icon: c.unread ? <Eye className="size-4" /> : <EyeOff className="size-4" />, onSelect: () => onPatchChat(c, { unread: !c.unread }) },
            ...(c.kind === "multi" ? [] : [{ label: t("chatMenu.fork"), icon: <GitFork className="size-4" />, onSelect: () => onForkChat(c) }]),
            // Chat section: a conversation keeps its own memory unless the user shares another's
            ...(onShareMemory ? [{ label: c.memoryFrom ? t("chatMenu.memoryShared") : t("chatMenu.shareMemory"), icon: <Brain className="size-4" />, onSelect: () => onShareMemory(c) }] : []),
            // Markdown with the files kept from it, in a zip (the share sheet on an iPad)
            { label: t("chatMenu.export"), icon: <Download className="size-4" />, onSelect: () => void saveToDevice(`/api/chats/${c.id}/export`, `${c.title.replace(/[\\/:*?"<>|]+/g, " ").trim().slice(0, 80) || "conversation"}.zip`, "application/zip") },
            { label: c.archived ? t("chatMenu.unarchive") : t("chatMenu.archive"), icon: c.archived ? <ArchiveRestore className="size-4" /> : <Archive className="size-4" />, divider: true, onSelect: () => onPatchChat(c, { archived: !c.archived }) },
            { label: t("chatMenu.delete"), icon: <Trash2 className="size-4" />, danger: true, onSelect: () => onDeleteChat(c) },
          ]}
        />
      )}
    </li>
  );
}
